/**
 * @file store/funcStore.tsx
 * @description 功能权限点缓存（Zustand）：提供 hasFuncPermission 辅助判断，避免重复拉取权限。
 */
import { getPermissionsApi } from '@/controllers/API/common'
import { handlePermissionToFrontend } from '@/permissions/utils'
import { showMessage } from '@/components/antd-comp/message'
import { create } from 'zustand'

type funcsState = {
  needFuncAuth: boolean
  funcCodes: string[]
}

type Actions = {
  /**
   * 设置是否启用功能点权限校验
   * @param status true 启用；false 关闭
   */
  setNeedFuncAuth: (status: boolean) => void
  /**
   * 写入用户功能点代码集合
   * @param codes 权限码数组
   * @returns Promise<void>
   */
  setFuncCodes: (codes: string[]) => Promise<any>
  /**
   * 重新从后端拉取当前用户权限码并覆盖本地缓存
   * @returns 最新前端权限码数组
   */
  refreshFuncCodes: () => Promise<string[]>
  /**
   * 存在一个有效code码即可访问对应服务
   * @param codes 需要校验的功能码集合
   * @param prompt 无权限时是否弹提示，默认 true
   * @returns 是否有权限
   */
  hasFuncPermission: (codes: string[], prompt?: boolean) => boolean
}

/**
 * 用户权限列表，使用lenovo跳转登录时设置needAuth为true，启用Permission校验，本地登录暂不校验权限
 */
export const useFuncsStore = create<funcsState & Actions>(set => ({
  needFuncAuth: false,
  funcCodes: [],
  setNeedFuncAuth: status => {
    set({ needFuncAuth: status })
  },
  setFuncCodes: codes => {
    set({ funcCodes: [...codes] })
    return Promise.resolve()
  },
  refreshFuncCodes: async () => {
    const currentState = useFuncsStore.getState()
    if (!currentState.needFuncAuth) {
      return currentState.funcCodes
    }

    const res = await getPermissionsApi()
    const backendPermissionList = Array.isArray(res) ? res : []
    const permissionList = handlePermissionToFrontend(backendPermissionList)
    set({ funcCodes: [...permissionList] })
    return permissionList
  },
  hasFuncPermission: (codes: string[], prompt = true) => {
    console.log(`>>>检查【${codes}】权限`)
    const currentState = useFuncsStore.getState()
    if (!currentState.needFuncAuth) {
      return true
    }

    for (let i = 0; i < codes.length; i++) {
      if (currentState.funcCodes.includes(codes[i])) {
        return true
      }
    }
    if (prompt) {
      showMessage({
        content: '暂无该功能权限，请联系企业超级管理员',
        type: 'warning'
      })
    }
    return false
  }
}))
